import React, { useEffect, useRef, useState } from "react";
import { API_URLS } from "../../../../apiUrl";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axiosInstance from "../../../../components/axiosInstance";
import CustomSelect from "../../../../components/CustomSelect";

type ViewType = "list" | "form" | "csv";

interface ConsumercreateFormProps {
  setView: React.Dispatch<React.SetStateAction<ViewType>>;
  streetId: number;
  refreshList: () => void;
  consumerId?: number | null;
  isEditing?: boolean;
  onEditDone?: () => void;
  municipalityId: any;
}

interface Category {
  id: number;
  name: string;
  category_initial?: string;
}

interface Mouza {
  id: number;
  mouza_name: string;
  ward_id: number;
  ward_no: string;
}

interface FormErrors {
  consumer_name?: string;
  connection_number?: string;
  connection_start_date?: string;
  ferule_size?: string;
  consumer_category_id?: string;
  phone_number?: any;
  mouza_id?: string;
}

interface FarralRate {
  rate_id: number;
  ferule_size: string;
  rate: number;
}

interface FarralCategory {
  category_id: number;
  name: string;
  rates: FarralRate[];
}

const today = new Date().toISOString().split("T")[0];

const ConsumercreateForm: React.FC<ConsumercreateFormProps> = ({
  setView,
  streetId,
  refreshList,
  consumerId = null,
  isEditing = false,
  onEditDone,
  municipalityId
}) => {
  const [formData, setFormData] = useState({
    consumer_name: "",
    connection_number: "",
    holding_number: "",
    connection_start_date: "",
    opening_due_amount: 0,
    ferule_size: "",
    rate_id: "",
    consumer_category_id: "",
    street_id: streetId,
    phone_number: "",
    mouza_id: "",
    municipality_id: municipalityId
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [categories, setCategories] = useState<Category[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [farralSize, setFarralSize] = useState<FarralCategory[]>([]);
  const [mouzaList, setMouzaList] = useState<Mouza[]>([]);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const hasNavigated = useRef(false);


  // ─── Edit mode: fetch existing consumer details ───────────────────────────
  useEffect(() => {
    if (isEditing && consumerId) {
      setLoadingDetails(true);
      axiosInstance
        .post(API_URLS.consumerDetail, { connection_id: consumerId, municipality_id : municipalityId })
        .then((res) => {
          const d = res.data?.data ?? res.data;
          const feruleSizeValue = d.ferule_size ?? "";
          const categoryIdValue = String(d.consumer_category_id ?? "");
              
          setFormData({
            consumer_name: d.consumer_name ?? "",
            connection_number: d.connection_number ?? "",
            holding_number: d.holding_number ?? "",
            connection_start_date: d.connection_start_date?.split("T")[0] ?? "",
            opening_due_amount: Number(d.opening_due_amount) || 0,
            ferule_size: feruleSizeValue,
            rate_id: d.rate_id ? String(d.rate_id) : "",
            consumer_category_id: categoryIdValue,
            street_id: streetId,
            phone_number: d.phone_number ?? "",
            mouza_id: String(d.mouza_id ?? ""),
            municipality_id : municipalityId
          });
        })
        .catch((err) => {
          console.error("Detail fetch error:", err);
          toast.error("Failed to load consumer details");
        })
        .finally(() => setLoadingDetails(false));
    }
  }, [isEditing, consumerId, municipalityId, streetId]);

  // ─── Validation ───────────────────────────────────────────────────────────
  const validate = (): FormErrors => {
    const errs: FormErrors = {};
    if (!formData.consumer_name.trim())
      errs.consumer_name = "Consumer name is required";
    if (!formData.connection_number.trim())
      errs.connection_number = "Connection number is required";
    else if (formData.connection_number.trim().length > 7)
      errs.connection_number = "Connection number must be 7 characters or less";
    if (!formData.connection_start_date)
      errs.connection_start_date = "Connection start date is required";
    else if (formData.connection_start_date > today)
      errs.connection_start_date = "Future date is not allowed";
    if (!formData.ferule_size)
      errs.ferule_size = "Please select a Ferrule size";
    if (!formData.consumer_category_id)
      errs.consumer_category_id = "Please select a consumer category";
    return errs;
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name as keyof FormErrors]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const handleFeruleSizeChange = (value: string) => {
    const selectedCategory = farralSize.find(
      (cat) => cat.category_id === Number(formData.consumer_category_id)
    );
    
   const selectedRate = selectedCategory?.rates.find(
    (r) => String(r.rate_id) === value // ✅ match by rate_id
  );

    
     setFormData((prev) => ({
    ...prev,
    ferule_size: selectedRate?.ferule_size || "",
    rate_id: value 
  }));
  };

  // Handle category change - reset ferule_size and rate_id
  const handleCategoryChange = (value: string) => {
    setFormData((prev) => ({
      ...prev,
      consumer_category_id: value,
      ferule_size: "",
      rate_id: ""
    }));
    if (errors.consumer_category_id) {
      setErrors((prev) => ({ ...prev, consumer_category_id: undefined }));
    }
  };

  // ─── Error extractor ──────────────────────────────────────────────────────
  const extractErrorMessage = (error: any): string => {
    const responseData = error?.response?.data;
    if (!responseData) return "Something went wrong. Please try again.";
    if (typeof responseData === "string" && responseData.trim())
      return responseData.trim();
    if (responseData.message && typeof responseData.message === "string")
      return responseData.message;
    if (responseData.error && typeof responseData.error === "string")
      return responseData.error;
    if (responseData.errors && typeof responseData.errors === "object") {
      const firstField = Object.keys(responseData.errors)[0];
      if (firstField) {
        const fieldErrors = responseData.errors[firstField];
        if (Array.isArray(fieldErrors) && fieldErrors.length > 0)
          return fieldErrors[0];
        if (typeof fieldErrors === "string") return fieldErrors;
      }
    }
    if (
      responseData.data?.message &&
      typeof responseData.data.message === "string"
    )
      return responseData.data.message;
    if (
      responseData.data?.error &&
      typeof responseData.data.error === "string"
    )
      return responseData.data.error;
    return "Something went wrong. Please try again.";
  };

  // ─── Submit: create or update ─────────────────────────────────────────────
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    if (submitting) return;
    setSubmitting(true);

    // Calculate rate_id based on current selection
    let finalRateId = formData.rate_id;
    
    // If ferule_size is selected but rate_id is empty, try to find the rate_id
    if (formData.ferule_size && !finalRateId) {
      const selectedCategory = farralSize.find(
        (cat) => cat.category_id === Number(formData.consumer_category_id)
      );
      const selectedRate = selectedCategory?.rates.find(
        (r) => r.ferule_size === formData.ferule_size
      );
      if (selectedRate) {
        finalRateId = String(selectedRate.rate_id);
      }
    }

    const payload = {
      consumer_name: formData.consumer_name,
      connection_number: formData.connection_number,
      holding_number: formData.holding_number,
      connection_start_date: formData.connection_start_date,
      opening_due_amount: Number(formData.opening_due_amount),
      ferule_size: formData.ferule_size,
      rate_id: finalRateId ? Number(finalRateId) : null,
      consumer_category_id: Number(formData.consumer_category_id),
      phone_number: formData.phone_number,
      mouza_id: formData.mouza_id ? Number(formData.mouza_id) : null,
      municipality_id: formData.municipality_id ? Number(formData.municipality_id) : null,
    };

    try {
      if (isEditing && consumerId) {
        const updatePayload = {
          ...payload,
          id: consumerId,
          street_id: streetId,
        };
        console.log("Update payload:", updatePayload);
        await axiosInstance.post(API_URLS.consumerUpdate, updatePayload);
        if (!hasNavigated.current) {
          hasNavigated.current = true;
          toast.success("Consumer updated successfully", { autoClose: 1500 });
          setTimeout(() => {
            onEditDone?.();
            refreshList();
            setView("list");
          }, 1600);
        }
      } else {
        const createPayload = {
          ...payload,
          street_id: streetId,
        };
        console.log("Create payload:", createPayload);
        await axiosInstance.post(API_URLS.consumerCreate, createPayload);
        if (!hasNavigated.current) {
          hasNavigated.current = true;
          toast.success("Consumer added successfully", { autoClose: 1500 });
          setTimeout(() => {
            refreshList();
            setView("list");
          }, 1600);
        }
      }
    } catch (error: any) {
      console.error("Submit error:", error);
      toast.error(extractErrorMessage(error), { autoClose: 3000 });
    } finally {
      setSubmitting(false);
    }
  };

  // ─── Cancel ───────────────────────────────────────────────────────────────
  const handleCancel = () => {
    onEditDone?.();
    setView("list");
  };

  // ─── Fetch categories ─────────────────────────────────────────────────────
  useEffect(() => {
    axiosInstance
      .post(API_URLS.consumercatagoryapi)
      .then((response) => {
        const raw = response.data;
        setCategories(Array.isArray(raw) ? raw : raw?.data ?? []);
      })
      .catch((err) => {
        console.error("Category fetch error:", err);
        toast.error("Failed to load consumer categories");
      });
  }, []);

  // ─── Fetch ferule sizes ───────────────────────────────────────────────────
  useEffect(() => {
    axiosInstance
      .post(API_URLS.farralListingapi)
      .then((res) => setFarralSize(res.data.data.category || []))
      .catch(console.error);
  }, []);

  // ─── Fetch mouza list ─────────────────────────────────────────────────────
  useEffect(() => {
    if (!municipalityId) {
      setMouzaList([]);
      return;
    }
    
    axiosInstance
      .post(API_URLS.mouzafetch, { municipality_id: municipalityId })
      .then((res) => {
        const rows: Mouza[] = res.data?.data?.rows ?? [];
        setMouzaList(rows);
      })
      .catch((err) => {
        console.error("Mouza fetch error:", err);
        toast.error("Failed to load mouza list");
      });
  }, [municipalityId]);

  // Fix rate_id when farralSize data loads and we're in edit mode
  useEffect(() => {
    if (isEditing && farralSize.length > 0 && formData.consumer_category_id && formData.ferule_size) {
      const selectedCategory = farralSize.find(
        (cat) => cat.category_id === Number(formData.consumer_category_id)
      );
      const selectedRate = selectedCategory?.rates.find(
        (r) => r.ferule_size === formData.ferule_size
      );
      if (selectedRate && !formData.rate_id) {
        setFormData((prev) => ({
          ...prev,
          rate_id: String(selectedRate.rate_id)
        }));
      }
    }
  }, [farralSize, isEditing, formData.consumer_category_id, formData.ferule_size]);

  const selectedCategory = farralSize.find(
    (cat) => cat.category_id === Number(formData.consumer_category_id)
  );
  
  const filteredFeruleSizes = selectedCategory?.rates || [];

  const inputClass = (field: keyof FormErrors) =>
    `border px-3 py-2 rounded focus:outline-none focus:ring-2 ${
      errors[field]
        ? "border-red-400 focus:ring-red-300 bg-red-50"
        : "border-gray-300 focus:ring-blue-300"
    }`;

  const feruleOptions = filteredFeruleSizes.map((item) => ({
    label: `${item.ferule_size}" (₹ ${item.rate})`,
    value: String(item.rate_id),
  }));

  const mouzaOptions = mouzaList.map((m) => ({
    label: `${m.mouza_name} (${m.ward_no})`,
    value: String(m.id),
  }));

  // ─── Loading skeleton ─────────────────────────────────────────────────────
  if (loadingDetails) {
    return (
      <div className="bg-white p-6 rounded-lg shadow border flex items-center justify-center h-40">
        <span className="text-gray-400 animate-pulse">Loading details...</span>
      </div>
    );
  }

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <>
      <ToastContainer position="top-right" />
      <div className="bg-white p-6 rounded-lg shadow border">
        <h2 className="text-xl font-semibold mb-4">
          {isEditing ? "Edit Connection" : "Add Connection"}
        </h2>

        <form onSubmit={handleSubmit} className="grid grid-cols-2 gap-4">

          {/* Consumer Name */}
          <div className="flex flex-col">
            <label className="mb-1 font-medium text-sm">
              Consumer Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="consumer_name"
              placeholder="Enter Connection Name"
              value={formData.consumer_name}
              onChange={handleChange}
              className={inputClass("consumer_name")}
              maxLength={40}
            />
            {errors.consumer_name && (
              <p className="text-red-500 text-xs mt-1">{errors.consumer_name}</p>
            )}
          </div>

          {/* Connection Number */}
          <div className="flex flex-col">
            <label className="mb-1 font-medium text-sm">
              Connection Number <span className="text-red-500">*</span>
              <span className="text-gray-400 font-normal ml-1">(max 7 chars)</span>
            </label>
            <input
              type="text"
              name="connection_number"
              placeholder="e.g. CONN-01"
              value={formData.connection_number}
              onChange={handleChange}
              maxLength={7}
              minLength={6}
              className={inputClass("connection_number")}
            />
            <div className="flex justify-between items-center mt-1">
              {errors.connection_number ? (
                <p className="text-red-500 text-xs">{errors.connection_number}</p>
              ) : (
                <span />
              )}
              <span className="text-xs text-gray-400 ml-auto">
                {formData.connection_number.length}/7
              </span>
            </div>
          </div>

          {/* Holding Number */}
          <div className="flex flex-col">
            <label className="mb-1 font-medium text-sm">
              Holding Number
              <span className="text-gray-400 font-normal ml-1">(optional)</span>
            </label>
            <input
              type="text"
              name="holding_number"
              placeholder="Enter Holding Number"
              value={formData.holding_number}
              onChange={handleChange}
              className="border border-gray-300 px-3 py-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-300"
              maxLength={10}
            />
          </div>

          {/* Connection Start Date */}
          <div className="flex flex-col">
            <label className="mb-1 font-medium text-sm">
              Connection Start Date <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              name="connection_start_date"
              value={formData.connection_start_date}
              onChange={handleChange}
              max={today}
              className={inputClass("connection_start_date")}
            />
            {errors.connection_start_date && (
              <p className="text-red-500 text-xs mt-1">
                {errors.connection_start_date}
              </p>
            )}
          </div>

          {/* Opening Due Amount */}
          <div className="flex flex-col">
            <label className="mb-1 font-medium text-sm">
              Opening Due Amount <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              name="opening_due_amount"
              placeholder="Enter Opening Due Amount"
              value={formData.opening_due_amount}
              onChange={(e) => {
                if (e.target.value.length <= 7) handleChange(e);
              }}
              className="border border-gray-300 px-3 py-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-300"
            />
          </div>

          {/* Phone Number */}
          <div className="flex flex-col">
            <label className="mb-1 font-medium text-sm">Phone Number</label>
            <input
              type="number"
              name="phone_number"
              placeholder="Enter 10 Digit Phone Number"
              value={formData.phone_number}
              onChange={(e) => {
                if (e.target.value.length <= 10) handleChange(e);
              }}
              className="border border-gray-300 px-3 py-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-300"
            />
          </div>

          {/* Connection Category */}
          <div className="flex flex-col">
            <label className="mb-1 font-medium text-sm">
              Connection Category <span className="text-red-500">*</span>
            </label>
            <CustomSelect
              name="consumer_category_id"
              value={formData.consumer_category_id}
              onChange={(e:any) => handleCategoryChange(e.target.value)}
              className={inputClass("consumer_category_id")}
              placeholder="-- Select Connection Category --"
              options={categories.map((cat) => ({
                label: cat.name,
                value: String(cat.id),
              }))}
            />
            {errors.consumer_category_id && (
              <p className="text-red-500 text-xs mt-1">
                {errors.consumer_category_id}
              </p>
            )}
          </div>

          {/* Ferule Size */}
          <div className="flex flex-col">
            <label className="mb-1 font-medium text-sm">
              Ferrule Size <span className="text-red-500">*</span>
            </label>
            <CustomSelect
              name="ferule_size"
              value={formData.rate_id}
              options={feruleOptions}
              onChange={(e:any) => handleFeruleSizeChange(e.target.value)}
              placeholder="-- Select Ferrule Size --"
            />
            {errors.ferule_size && (
              <p className="text-red-500 text-xs mt-1">{errors.ferule_size}</p>
            )}
          </div>

          {/* ─── Last row: Mouza (left) + Buttons (right) ──────────────────── */}
          <div className="flex flex-col">
            <label className="mb-1 font-medium text-sm">
              Mouza
              <span className="text-gray-400 font-normal ml-1">(optional)</span>
            </label>
            <CustomSelect
              name="mouza_id"
              value={formData.mouza_id}
              options={mouzaOptions}
              onChange={handleChange}
              placeholder="-- Select Mouza --"
            />
            {errors.mouza_id && (
              <p className="text-red-500 text-xs mt-1">{errors.mouza_id}</p>
            )}
          </div>

          {/* Buttons — right column, same row as Mouza */}
          <div className="flex items-end justify-end gap-3">
            <button
              type="button"
              onClick={handleCancel}
              className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 disabled:cursor-not-allowed text-white px-4 py-2 rounded"
            >
              {submitting
                ? isEditing
                  ? "Updating..."
                  : "Saving..."
                : isEditing
                ? "Update"
                : "Save"}
            </button>
          </div>

        </form>
      </div>
    </>
  );
};

export default ConsumercreateForm;